import {useContext, useEffect, useState, useRef, MutableRefObject} from 'react';
import {AuthContextType} from '../../contexts/auth/types';
import {AuthContext} from '../../contexts/auth/context';
import {matchPath, useHistory, useLocation} from 'react-router-dom';
import {
  UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE,
  CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE,
  CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE,
  UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE,
} from '../../events/event-types';
import {RelationshipInfo} from '../../backend/interfaces/relationship-info';
import {SessionAffiliationInfo} from '../../interfaces/session-affiliation-info';
import {ScreenPaths} from '../../enums/screen-path';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {Avatar, Menu, ListItemAvatar, ListItemText, ListItem, List} from '@mui/material';
import eventEmitter from '../../events/event-emitter';
import BusinessOutlinedIcon from '@mui/icons-material/Business';
import PersonOutlinedIcon from '@mui/icons-material/Person';
import {RoleDropdown} from '../role-dropdown/role-dropdown';

/**
 * Picker used on the header of our office.
 *
 * This component is used to select the current relationship and affiliation.
 */
export const HeaderPicker = (props: {
  id: string;
  caption: string;
  type: 'relationship' | 'affiliation';
  name?: string;
  picture?: string;
}) => {
  // Get context
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  const [anchoredElement, setAnchoredElement] = useState<any>(null);
  const open: boolean = Boolean(anchoredElement);

  // Get history and location
  let history = useHistory();
  let location = useLocation();

  // Hold ref
  const pendingAffiliationChangeRef: MutableRefObject<SessionAffiliationInfo | undefined> =
    useRef();
  const pendingRelationshipChangeRef: MutableRefObject<RelationshipInfo | undefined> = useRef();

  // Component did mount
  useEffect(() => {
    const onClearPendingSelectedAffiliationChanged = () => {
      if (pendingAffiliationChangeRef.current) {
        // Log
        console.debug('🧹 Cleaning pending selected affiliation change request.');

        // Clear ref
        pendingAffiliationChangeRef.current = undefined;
      }
    };

    const onUnlockPendingSelectedAffiliationChange = () => {
      if (pendingAffiliationChangeRef.current) {
        // Log
        console.debug('🟢 Unlocking pending selected affiliation change request.');

        // Update affiliation
        authContext.updateSelectedAffiliation(pendingAffiliationChangeRef.current);

        // Clear ref
        pendingAffiliationChangeRef.current = undefined;
      }
    };

    const onClearPendingSelectedRelationshipChange = () => {
      if (pendingRelationshipChangeRef.current) {
        // Log
        console.debug('🧹 Cleaning pending selected relationship change request.');

        // Clear ref
        pendingRelationshipChangeRef.current = undefined;
      }
    };

    const onUnlockPendingSelectedRelationshipChange = () => {
      if (pendingRelationshipChangeRef.current) {
        // Log
        console.debug('🟢 Unlocking pending selected relationship change request.');

        // Update relationship
        authContext.updateSelectedRelationship(pendingRelationshipChangeRef.current);

        // Clear ref
        pendingRelationshipChangeRef.current = undefined;
      }
    };

    // Listen to emitters
    eventEmitter.on(
      CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE,
      onClearPendingSelectedAffiliationChanged
    );
    eventEmitter.on(
      UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE,
      onUnlockPendingSelectedAffiliationChange
    );
    eventEmitter.on(
      CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE,
      onClearPendingSelectedRelationshipChange
    );
    eventEmitter.on(
      UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE,
      onUnlockPendingSelectedRelationshipChange
    );

    // Clean up
    return () => {
      eventEmitter.off(
        CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE,
        onClearPendingSelectedAffiliationChanged
      );
      eventEmitter.off(
        UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE,
        onUnlockPendingSelectedAffiliationChange
      );
      eventEmitter.off(
        CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE,
        onClearPendingSelectedRelationshipChange
      );
      eventEmitter.off(
        UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE,
        onUnlockPendingSelectedRelationshipChange
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unlock pending selected affiliation change
  useEffect(() => {
    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      if (
        matchPath(location.pathname, {
          path: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/:entityId${ScreenPaths.ProfessionalCaregiver_Activity}`,
          exact: true,
          strict: false,
        }) &&
        pendingAffiliationChangeRef.current
      ) {
        // Log
        console.debug('🟢 Updating the selected affiliation as the user requested.');

        // Update affiliation
        authContext.updateSelectedAffiliation(pendingAffiliationChangeRef.current);

        // Clear
        pendingAffiliationChangeRef.current = undefined;
      } else if (
        matchPath(location.pathname, {
          path: `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/:seniorId${ScreenPaths.InformalCaregiver_Activity}`,
          exact: true,
          strict: false,
        }) &&
        pendingRelationshipChangeRef.current
      ) {
        // Log
        console.debug('🟢 Updating the selected relationship as the user requested.');

        // Update relationship
        authContext.updateSelectedRelationship(pendingRelationshipChangeRef.current);

        // Clear
        pendingRelationshipChangeRef.current = undefined;
      }
    });

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Handle click on anchored element
  const handleClick = (event: any) => {
    setAnchoredElement(event.currentTarget);
  };

  // Handle close menu
  const handleClose = () => {
    setAnchoredElement(null);
  };

  // Update the selected relationship
  const handleUpdateSelectedRelationship = (relationship: RelationshipInfo) => {
    if (
      relationship &&
      relationship.id.toString() !== authContext.state.session?.selectedRelationship?.id.toString()
    ) {
      if (
        matchPath(location.pathname, {
          path: [
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_ProfileDetails}`,
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Notifications}`,
          ],
          exact: true,
          strict: false,
        })
      ) {
        // Log
        console.debug(
          "🔄 The user tried to change the selected relationship but they can't stay on the current screen. Redirecting to the Activity screen before updating the selected relationship because the router prompt could be shown."
        );

        // Update pending selected relationship change to use it later on
        pendingRelationshipChangeRef.current = relationship;

        // Redirect
        history.push(
          `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${relationship.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`
        );

        // Close menu
        handleClose();
      } else {
        // Update relationship
        authContext.updateSelectedRelationship(relationship);

        // Close menu
        handleClose();
      }
    }
  };

  // Update the selected affiliation
  const handleUpdateSelectedAffiliation = (affiliation: SessionAffiliationInfo) => {
    if (
      affiliation &&
      affiliation.id.toString() !== authContext.state.session?.selectedAffiliation?.id.toString()
    ) {
      if (
        matchPath(location.pathname, {
          path: [
            `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/:entityId${ScreenPaths.ProfessionalCaregiver_EntityServices}/:id`,
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_ProfileDetails}`,
            `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Notifications}`,
          ],
          exact: true,
          strict: false,
        })
      ) {
        // Log
        console.debug(
          "🔄 The user tried to change the selected affiliation but they can't stay on the current screen. Redirecting to the Activity screen before updating the selected affiliation because the router prompt could be shown."
        );

        // Update pending selected affiliation change to use it later on
        pendingAffiliationChangeRef.current = affiliation;

        // Redirect
        history.push(
          `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${affiliation.entity.id}${ScreenPaths.ProfessionalCaregiver_Activity}`
        );

        // Close menu
        handleClose();
      } else {
        // Update affiliation
        authContext.updateSelectedAffiliation(affiliation);

        // Close menu
        handleClose();
      }
    }
  };

  // Avatar sizes
  const avatarWidth: string = `28px`;
  const avatarHeight: string = `28px`;
  const avatarIconSize: string = `${
    avatarSizesAndCoefficients.avatarSizes.medium * avatarSizesAndCoefficients.iconSizeCoefficient
  }px`;
  const avatarBorderRadius: string =
    props.type === 'affiliation'
      ? `${
          avatarSizesAndCoefficients.avatarSizes.medium *
          avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
        }px`
      : `${
          avatarSizesAndCoefficients.avatarSizes.medium *
          avatarSizesAndCoefficients.borderRadiusCoefficients.circle
        }px`;

  // Single option
  const isSingleOption: boolean =
    ((props.type === 'affiliation' &&
      authContext.state.session?.affiliations &&
      authContext.state.session?.affiliations.length === 1) ||
      (props.type === 'relationship' &&
        authContext.state.session?.relationships &&
        authContext.state.session?.relationships.length === 1)) ??
    false;

  const dropdownInfo = {
    title: props.name!,
    picture: props.picture!,
    defaultIconName: BusinessOutlinedIcon,
  };

  return (
    <>
      <RoleDropdown
        info={dropdownInfo}
        active={open}
        handleClick={!isSingleOption ? handleClick : undefined}
        handleClose={!isSingleOption ? handleClose : undefined}
      />
      <Menu id={`${props.id}-menu`} anchorEl={anchoredElement} open={open} onClose={handleClose}>
        {props.type === 'relationship' ? (
          <List sx={{pt: 0, paddingTop: '8px'}}>
            {authContext.state.session?.relationships.map((relationship) => {
              return (
                <ListItem
                  button
                  key={relationship?.id}
                  onClick={() => handleUpdateSelectedRelationship(relationship)}
                  selected={
                    authContext.state.session?.selectedRelationship?.id.toString() ===
                    relationship?.id.toString()
                  }
                >
                  <ListItemAvatar
                    sx={{
                      minWidth: '0',
                      marginRight: '8px',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: avatarWidth,
                        height: avatarHeight,
                        borderRadius: avatarBorderRadius,
                        backgroundColor: '#FFFFFF',
                        color: 'rgba(70, 70, 70, 1)',
                      }}
                      alt={relationship?.withUser.name}
                      src={relationship?.withUser?.picture ? relationship.withUser.picture : ''}
                    >
                      <PersonOutlinedIcon
                        sx={{
                          fontSize: avatarIconSize,
                        }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={relationship?.withUser.name} />
                </ListItem>
              );
            })}
          </List>
        ) : undefined}
        {props.type === 'affiliation' ? (
          <List sx={{pt: 0, paddingTop: '8px'}}>
            {authContext.state.session?.affiliations.map((affiliation) => {
              return (
                <ListItem
                  button
                  key={affiliation?.id}
                  onClick={() => handleUpdateSelectedAffiliation(affiliation)}
                  selected={
                    authContext.state.session?.selectedAffiliation?.id.toString() ===
                    affiliation?.id.toString()
                  }
                >
                  <ListItemAvatar
                    sx={{
                      minWidth: '0',
                      marginRight: '8px',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: avatarWidth,
                        height: avatarHeight,
                        borderRadius: avatarBorderRadius,
                        backgroundColor: '#FFFFFF',
                        color: 'rgba(70, 70, 70, 1)',
                      }}
                      alt={affiliation?.entity?.name}
                      src={affiliation?.entity?.picture ? affiliation.entity.picture : ''}
                    >
                      <BusinessOutlinedIcon
                        sx={{
                          fontSize: avatarIconSize,
                        }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={affiliation?.entity?.shortName || affiliation?.entity?.name}
                  />
                </ListItem>
              );
            })}
          </List>
        ) : undefined}
      </Menu>
    </>
  );
};
